// Sample Forge as an in-rail surface — the full authoring pipeline
// (verb tabs → buffer/trim → commit) re-homed inside the MINE
// section of the left rail. This file is the dissolved
// `ForgePanel.tsx`: the modal shell is gone, but its concerns
// (buffer + meta state, trim chain, save) are unchanged.
//
// The MINE section's parent decides when this component is mounted —
// "list mode" mounts MySamples, "forge mode" mounts MineForge. The
// `onBack` callback flips back to list mode (used by the back chip
// at the top, the Discard button, and an automatic call after a
// successful commit).

import { useCallback, useMemo, useState } from 'react';
import { getAudioContext, getSampleStorage } from '../../strudel';
import { useSampleStore } from './SampleStoreContext';
import { commit } from '../../samples/commit';
import {
  fade,
  fromPcm,
  normalize,
  reverse,
  toPcm,
  trim,
  type PcmBuffer,
} from '../../samples/pipeline';
import type { License } from '../../samples/types';
import { WaveformPad, type MarkerKind } from '../../samples/ui/WaveformPad';
import {
  TrimControls,
  DEFAULT_TRIM_STATE,
  type TrimState,
} from '../../samples/ui/TrimControls';
import { BounceVerb } from '../../samples/forge/verbs/BounceVerb';
import { ChopVerb } from '../../samples/forge/verbs/ChopVerb';
import { ConvolveVerb } from '../../samples/forge/verbs/ConvolveVerb';
import { DrawVerb } from '../../samples/forge/verbs/DrawVerb';
import { FreezeVerb } from '../../samples/forge/verbs/FreezeVerb';
import { ImportVerb } from '../../samples/forge/verbs/ImportVerb';
import { MangleVerb } from '../../samples/forge/verbs/MangleVerb';
import { PitchVerb } from '../../samples/forge/verbs/PitchVerb';
import { RecordVerb } from '../../samples/forge/verbs/RecordVerb';
import { SidechainVerb } from '../../samples/forge/verbs/SidechainVerb';
import { SpeechVerb } from '../../samples/forge/verbs/SpeechVerb';
import { StretchVerb } from '../../samples/forge/verbs/StretchVerb';
import { SynthVerb } from '../../samples/forge/verbs/SynthVerb';
import type { VerbId, VerbMeta, VerbProps } from '../../samples/forge/types';
import { VERB_DEFINITIONS } from '../../samples/forge/types';

export type MineForgeProps = {
  /** Status reporter for the StatusBar. */
  onStatus: (message: string, level?: 'ok' | 'warn' | 'err' | '') => void;
  /** Flip MINE back to list mode. Called by the back chip, the
   *  Discard button, and automatically after a successful commit. */
  onBack: () => void;
};

type CommitState = {
  name: string;
  tags: string;
  license: License;
};

// Default license is 'unspecified' rather than 'self': claiming
// ownership by default would lie for imported files, and a user who
// made the sound themselves only has to click once to upgrade. The
// asymmetry favours honesty over convenience.
const DEFAULT_COMMIT: CommitState = {
  name: '',
  tags: '',
  license: 'unspecified',
};

export function MineForge({ onStatus, onBack }: MineForgeProps) {
  const [verbId, setVerbId] = useState<VerbId>('record');
  const [sourceBuffer, setSourceBuffer] = useState<AudioBuffer | null>(null);
  const [meta, setMeta] = useState<VerbMeta | null>(null);
  const [inFrame, setInFrame] = useState(0);
  const [outFrame, setOutFrame] = useState(0);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [trimState, setTrimState] = useState<TrimState>(DEFAULT_TRIM_STATE);
  const [commitState, setCommitState] = useState<CommitState>(DEFAULT_COMMIT);
  const [committing, setCommitting] = useState(false);
  const { refresh: refreshSampleStore } = useSampleStore();

  // Engine handles (pull once per render; they're module singletons
  // so the identity is stable after boot).
  const audioContext = getAudioContext();
  const storage = getSampleStorage();

  const handleBuffer = useCallback(
    (buffer: AudioBuffer, nextMeta: VerbMeta) => {
      setSourceBuffer(buffer);
      setMeta(nextMeta);
      setInFrame(0);
      setOutFrame(buffer.length);
      setTrimState(DEFAULT_TRIM_STATE);
      setCommitState((prev) => ({
        ...prev,
        name: nextMeta.suggestedName ?? prev.name,
      }));
    },
    [],
  );

  const handleVerbChange = useCallback((next: VerbId) => {
    setVerbId(next);
    setSourceBuffer(null);
    setMeta(null);
    setInFrame(0);
    setOutFrame(0);
    setTrimState(DEFAULT_TRIM_STATE);
  }, []);

  // Apply the trim chain to the source buffer to produce the preview.
  const previewBuffer = useMemo(() => {
    if (!sourceBuffer || !audioContext) return null;
    let pcm: PcmBuffer = toPcm(sourceBuffer);
    pcm = trim(pcm, {
      startSeconds: inFrame / sourceBuffer.sampleRate,
      endSeconds: outFrame / sourceBuffer.sampleRate,
    });
    pcm = fade(pcm, {
      inSeconds: trimState.fadeInMs / 1000,
      outSeconds: trimState.fadeOutMs / 1000,
    });
    if (trimState.normalize) pcm = normalize(pcm);
    if (trimState.reverse) pcm = reverse(pcm);
    return fromPcm(audioContext, pcm);
  }, [sourceBuffer, audioContext, inFrame, outFrame, trimState]);

  const handleMarker = useCallback((kind: MarkerKind, frame: number) => {
    if (kind === 'in') setInFrame(frame);
    else setOutFrame(frame);
  }, []);

  const handleDiscard = useCallback(() => {
    setSourceBuffer(null);
    setMeta(null);
    setCommitState(DEFAULT_COMMIT);
    setTrimState(DEFAULT_TRIM_STATE);
  }, []);

  const handleSave = useCallback(async () => {
    if (!previewBuffer || !audioContext || !storage || !meta) {
      onStatus('Cannot save — engine or buffer missing.', 'err');
      return;
    }
    const name = commitState.name.trim();
    if (!name) {
      onStatus('Enter a name before saving.', 'warn');
      return;
    }
    setCommitting(true);
    try {
      const tags = commitState.tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      const sample = await commit(
        {
          buffer: previewBuffer,
          name,
          origin: meta.origin,
          tags,
          license: commitState.license,
          pitchedRootMidi: meta.pitchedRootMidi,
          sourceSampleIds: meta.sourceSampleIds,
        },
        storage,
        audioContext,
      );
      onStatus(`${sample.name} saved — try s("${sample.name}").`, 'ok');
      setSourceBuffer(null);
      setMeta(null);
      setCommitState(DEFAULT_COMMIT);
      setTrimState(DEFAULT_TRIM_STATE);
      refreshSampleStore();
      onBack();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      onStatus('Save failed: ' + msg, 'err');
    } finally {
      setCommitting(false);
    }
  }, [
    previewBuffer,
    audioContext,
    storage,
    meta,
    commitState,
    onStatus,
    refreshSampleStore,
    onBack,
  ]);

  const verbProps: VerbProps = {
    audioContext,
    storage,
    onBuffer: handleBuffer,
    onStatus,
  };

  return (
    <div className="relative w-full h-full bg-zinc-950 flex flex-col min-h-0">
      <BackBar onBack={onBack} />
      <VerbTabs active={verbId} onChange={handleVerbChange} />

      <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin">
        {sourceBuffer ? (
          <BufferPanel
            buffer={sourceBuffer}
            previewBuffer={previewBuffer}
            audioContext={audioContext}
            inFrame={inFrame}
            outFrame={outFrame}
            loopEnabled={loopEnabled}
            onLoopToggle={setLoopEnabled}
            onMarker={handleMarker}
            trimState={trimState}
            onTrimState={setTrimState}
          />
        ) : (
          <ActiveVerb verbId={verbId} verbProps={verbProps} />
        )}
      </div>

      <CommitBar
        enabled={sourceBuffer !== null && !committing}
        state={commitState}
        onChange={setCommitState}
        onSave={handleSave}
        onDiscard={handleDiscard}
        saving={committing}
      />
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function BackBar({ onBack }: { onBack: () => void }) {
  return (
    <div className="px-3 pt-3 pb-2 flex items-center">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-yellow-300 transition-colors"
      >
        <span aria-hidden="true">←</span> My samples
      </button>
    </div>
  );
}

function VerbTabs({
  active,
  onChange,
}: {
  active: VerbId;
  onChange: (id: VerbId) => void;
}) {
  return (
    <nav
      className="flex flex-wrap gap-1 px-3 pb-2 border-b border-zinc-800"
      role="tablist"
      aria-label="Forge mode"
    >
      {VERB_DEFINITIONS.map((v) => (
        <button
          type="button"
          key={v.id}
          role="tab"
          aria-selected={active === v.id}
          onClick={() => onChange(v.id)}
          title={v.description}
          className={
            'px-2 py-1 text-[9px] uppercase tracking-[0.18em] rounded-sm border transition-colors ' +
            (active === v.id
              ? 'bg-yellow-300 border-yellow-300 text-zinc-950 font-semibold'
              : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700')
          }
        >
          {v.label}
        </button>
      ))}
    </nav>
  );
}

function ActiveVerb({
  verbId,
  verbProps,
}: {
  verbId: VerbId;
  verbProps: VerbProps;
}) {
  switch (verbId) {
    case 'import':
      return <ImportVerb {...verbProps} />;
    case 'record':
      return <RecordVerb {...verbProps} />;
    case 'bounce':
      return <BounceVerb {...verbProps} />;
    case 'synth':
      return <SynthVerb {...verbProps} />;
    case 'chop':
      return <ChopVerb {...verbProps} />;
    case 'mangle':
      return <MangleVerb {...verbProps} />;
    case 'speech':
      return <SpeechVerb {...verbProps} />;
    case 'draw':
      return <DrawVerb {...verbProps} />;
    case 'convolve':
      return <ConvolveVerb {...verbProps} />;
    case 'sidechain':
      return <SidechainVerb {...verbProps} />;
    case 'stretch':
      return <StretchVerb {...verbProps} />;
    case 'freeze':
      return <FreezeVerb {...verbProps} />;
    case 'pitch':
      return <PitchVerb {...verbProps} />;
  }
}

type BufferPanelProps = {
  buffer: AudioBuffer;
  previewBuffer: AudioBuffer | null;
  audioContext: AudioContext | null;
  inFrame: number;
  outFrame: number;
  loopEnabled: boolean;
  onLoopToggle: (next: boolean) => void;
  onMarker: (kind: MarkerKind, frame: number) => void;
  trimState: TrimState;
  onTrimState: (next: TrimState) => void;
};

function BufferPanel({
  buffer,
  previewBuffer,
  audioContext,
  inFrame,
  outFrame,
  loopEnabled,
  onLoopToggle,
  onMarker,
  trimState,
  onTrimState,
}: BufferPanelProps) {
  return (
    <div className="space-y-3">
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">Source</div>
      <WaveformPad
        buffer={buffer}
        inMarkerFrame={inFrame}
        outMarkerFrame={outFrame}
        loopEnabled={loopEnabled}
        onMarkerChange={onMarker}
        audioContext={audioContext}
      />
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">Trim</div>
      <TrimControls state={trimState} onChange={onTrimState} />
      <label className="inline-flex items-center gap-2 text-[10px] text-zinc-400 cursor-pointer">
        <input
          type="checkbox"
          checked={loopEnabled}
          onChange={(e) => onLoopToggle(e.target.checked)}
          className="accent-yellow-300"
        />
        <span className="uppercase tracking-[0.18em] text-[9px] text-zinc-500">
          Loop between markers
        </span>
      </label>
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">Preview</div>
      <WaveformPad
        buffer={previewBuffer}
        inMarkerFrame={0}
        outMarkerFrame={previewBuffer?.length ?? 0}
        audioContext={audioContext}
      />
    </div>
  );
}

type CommitBarProps = {
  enabled: boolean;
  state: CommitState;
  onChange: (next: CommitState) => void;
  onSave: () => void;
  onDiscard: () => void;
  saving: boolean;
};

function CommitBar({
  enabled,
  state,
  onChange,
  onSave,
  onDiscard,
  saving,
}: CommitBarProps) {
  const patch = (diff: Partial<CommitState>) => onChange({ ...state, ...diff });
  return (
    <footer className="border-t border-zinc-800 bg-zinc-900 px-3 py-2.5 flex flex-col gap-2 shrink-0">
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">Name</span>
        <input
          type="text"
          value={state.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="mykick"
          className="bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-[12px] text-zinc-100 focus:border-yellow-300 focus:outline-none rounded-sm"
          disabled={!enabled}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">Tags</span>
        <input
          type="text"
          value={state.tags}
          onChange={(e) => patch({ tags: e.target.value })}
          placeholder="drum, kick"
          className="bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-[12px] text-zinc-100 focus:border-yellow-300 focus:outline-none rounded-sm"
          disabled={!enabled}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">License</span>
        <select
          value={state.license}
          onChange={(e) => patch({ license: e.target.value as License })}
          className="bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-[12px] text-zinc-100 focus:border-yellow-300 focus:outline-none rounded-sm"
          disabled={!enabled}
        >
          <option value="self">I made this</option>
          <option value="cc0">CC0</option>
          <option value="cc-by">CC-BY</option>
          <option value="unspecified">Unspecified</option>
        </select>
      </label>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onDiscard}
          disabled={!enabled}
          className="flex-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-sm"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!enabled}
          className="flex-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30 disabled:cursor-not-allowed rounded-sm"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </footer>
  );
}
